import type { MarkdownEditorContextValue } from './markdown-editor-context';
import type { NodeViewProps } from '@tiptap/react';
import type { MutableRefObject } from 'react';

import { Node } from '@tiptap/core';
import '@tiptap/markdown';
import { NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';

import { GalleryBlock } from '#components/document-viewer/gallery/gallery-block';
import {
  normalizeGalleryConfig,
  stringifyGalleryConfig,
} from '#components/document-viewer/gallery/gallery-config';

import {
  NP_GALLERY_TAG,
  parseMarkdownGalleryConfig,
  serializeMarkdownGalleryBlock,
  serializeMarkdownGalleryInnerConfig,
} from './markdown-gallery';

interface MarkdownGalleryNodeOptions {
  contextRef: MutableRefObject<MarkdownEditorContextValue>;
}

interface MarkdownGalleryNodeAttrs {
  data: ReturnType<typeof normalizeGalleryConfig>;
  rawConfig: string;
  parseError: string | null;
}

function normalizeGalleryAttrs(attrs: unknown): MarkdownGalleryNodeAttrs {
  const rec =
    attrs && typeof attrs === 'object'
      ? (attrs as Record<string, unknown>)
      : {};
  return {
    data: normalizeGalleryConfig(rec.data),
    rawConfig: typeof rec.rawConfig === 'string' ? rec.rawConfig : '',
    parseError: typeof rec.parseError === 'string' ? rec.parseError : null,
  };
}

function MarkdownGalleryNodeView({
  extension,
  node,
  selected,
  updateAttributes,
}: NodeViewProps) {
  const { contextRef } = extension.options as MarkdownGalleryNodeOptions;
  const context = contextRef.current;
  const attrs = normalizeGalleryAttrs(node.attrs);

  const handleEdit = () => {
    context.onRequestGalleryEdit?.({
      initialConfig: attrs.data,
      onCommit: async (next) => {
        const normalized = normalizeGalleryConfig(next);
        updateAttributes({
          data: normalized,
          rawConfig: stringifyGalleryConfig(normalized),
          parseError: null,
        });
      },
    });
  };

  return (
    <NodeViewWrapper
      className={
        selected
          ? 'markdown-gallery-node markdown-gallery-node--selected'
          : 'markdown-gallery-node'
      }
      contentEditable={false}
      data-drag-handle={false}
    >
      <GalleryBlock
        config={attrs.data}
        parseError={attrs.parseError}
        canEdit={Boolean(context.onRequestGalleryEdit)}
        onEdit={context.onRequestGalleryEdit ? handleEdit : undefined}
      />
    </NodeViewWrapper>
  );
}

export const MarkdownGalleryNode = Node.create<MarkdownGalleryNodeOptions>({
  name: 'npGallery',
  group: 'block',
  atom: true,
  selectable: true,
  isolating: true,
  defining: true,

  addOptions() {
    return {
      contextRef: { current: null as never },
    };
  },

  addAttributes() {
    return {
      data: {
        default: normalizeGalleryConfig({}),
      },
      rawConfig: {
        default: '',
      },
      parseError: {
        default: null,
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: NP_GALLERY_TAG,
        getAttrs: (element) => {
          if (!(element instanceof HTMLElement)) return false;

          const parsed = parseMarkdownGalleryConfig(element.textContent ?? '');
          return {
            data: parsed.data,
            rawConfig: parsed.rawConfig,
            parseError: parsed.parseError,
          };
        },
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    const attrs = normalizeGalleryAttrs(HTMLAttributes);
    return [NP_GALLERY_TAG, serializeMarkdownGalleryInnerConfig(attrs)];
  },

  renderMarkdown(node) {
    const attrs = normalizeGalleryAttrs(node?.attrs);
    return serializeMarkdownGalleryBlock(attrs);
  },

  addNodeView() {
    return ReactNodeViewRenderer(MarkdownGalleryNodeView);
  },
});
